// Palette catégorielle des domaines disciplinaires, construite dans la
// famille du thème : clarté et saturation voisines des accents du thème (OKLCH),
// teintes espacées sur le cercle et clartés légèrement étagées pour rester
// distinguables, y compris en vision des couleurs déficiente.
// Une séance porte sa couleur même sans créneau d'emploi du temps
// correspondant. Partagée par le calendrier et l'éditeur d'emploi du temps
// pour que l'un ressemble à l'autre.
const DOMAIN_COLORS: Record<string, string> = {
	// Élémentaire (cycles 2 et 3)
	français: 'oklch(56% 0.11 255)',
	mathématiques: 'oklch(55% 0.13 20)',
	'histoire-géographie': 'oklch(66% 0.11 75)',
	'sciences et technologie': 'oklch(54% 0.11 300)',
	'questionner le monde': 'oklch(54% 0.11 300)',
	'éducation physique et sportive': 'oklch(62% 0.10 155)',
	'arts plastiques': 'oklch(60% 0.11 350)',
	'éducation musicale': 'oklch(63% 0.09 210)',
	'langues vivantes': 'oklch(61% 0.09 110)',
	'enseignement moral et civique': 'oklch(52% 0.09 280)',
	'éducation à la vie affective et relationnelle': 'oklch(60% 0.09 185)',
	// Maternelle (cycle 1, programmes 2025) : chaque domaine reprend la teinte
	// de son parent disciplinaire de l'élémentaire.
	'le développement et la structuration du langage oral et écrit': 'oklch(56% 0.11 255)',
	"l'acquisition des premiers outils mathématiques": 'oklch(55% 0.13 20)',
	"agir, s'exprimer, comprendre à travers les activités physiques": 'oklch(62% 0.10 155)',
	"agir, s'exprimer, comprendre à travers les activités artistiques": 'oklch(60% 0.11 350)',
	"se repérer dans le temps et l'espace": 'oklch(66% 0.11 75)',
	'découvrir le monde du vivant, de la matière et des objets': 'oklch(54% 0.11 300)'
};

const NEUTRAL = 'oklch(58% 0.02 75)';

/** Clé de comparaison : casse et apostrophes (droite vs typographique)
 * ignorées — les programmes officiels mélangent les deux graphies. */
function canon(s: string): string {
	return s.toLowerCase().replace(/’/g, "'");
}

/** Couleur d'un domaine, ou null s'il est inconnu (intitulés voisins compris,
 * via une correspondance sur le début de l'intitulé). */
export function domainColor(domain: string): string | null {
	if (!domain) return null;
	const key = canon(domain);
	if (DOMAIN_COLORS[key]) return DOMAIN_COLORS[key];
	const match = Object.keys(DOMAIN_COLORS).find((k) => key.startsWith(k.slice(0, 6)));
	return match ? DOMAIN_COLORS[match] : null;
}

/** Couleur d'affichage garantie (repli neutre pour un domaine non répertorié). */
export function domainColorOrNeutral(domain: string): string {
	return domainColor(domain) ?? NEUTRAL;
}

/** Fond et bordure teintés d'une couleur de base : la même recette partout
 * (cartes du journal, créneaux de l'emploi du temps) pour que les surfaces
 * se répondent. Le fond reste assez clair pour du texte ink.
 * Mélange en oklab (cartésien) : en oklch, l'interpolation de teinte fait le
 * tour du cercle — un bleu dilué dans le papier chaud ressortait vert sauge. */
export function tintFromColor(base: string): { background: string; border: string } {
	return {
		background: `color-mix(in oklab, ${base} 20%, var(--color-paper))`,
		border: `color-mix(in oklab, ${base} 55%, var(--color-paper))`
	};
}

/** Fond et bordure teintés d'un domaine, cohérents avec les cartes du calendrier. */
export function domainTint(domain: string): { background: string; border: string } {
	return tintFromColor(domainColorOrNeutral(domain));
}
